import axios from "axios";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "sof_admin_auth";

const AuthContext = createContext(null);

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.token || !parsed?.expires_at) return null;
    if (new Date(parsed.expires_at).getTime() <= Date.now()) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(() => readStored());
  const [checking, setChecking] = useState(Boolean(auth));

  // On mount, if we have a stored token verify it's still valid.
  useEffect(() => {
    let cancelled = false;
    async function verify() {
      if (!auth?.token) {
        setChecking(false);
        return;
      }
      try {
        await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${auth.token}` },
        });
        if (!cancelled) setChecking(false);
      } catch {
        if (!cancelled) {
          localStorage.removeItem(STORAGE_KEY);
          setAuth(null);
          setChecking(false);
        }
      }
    }
    verify();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (username, password) => {
    const { data } = await axios.post(`${API}/auth/login`, {
      username,
      password,
    });
    const next = {
      token: data.access_token,
      username: data.username,
      expires_at: data.expires_at,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setAuth(next);
    return next;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setAuth(null);
  }, []);

  const authHeaders = useMemo(
    () => (auth?.token ? { Authorization: `Bearer ${auth.token}` } : {}),
    [auth?.token]
  );

  const value = useMemo(
    () => ({
      auth,
      isAuthenticated: Boolean(auth?.token),
      checking,
      login,
      logout,
      authHeaders,
    }),
    [auth, checking, login, logout, authHeaders]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
